// M7.3 — inline nudge CTA card (ADR-007 D-S6, D-S14 impression hook).
//
// PENDING DESIGN SIGN-OFF. Mark has not signed this card. The layout
// is provisional (AGENT_SHELL_VISUAL_STATUS). Do not describe it as
// the final panel or M8 treatment.
//
// A nudge PROPOSES an action. The CTA click NEVER posts a mutation.
//
// Dispatch:
//   • open-dialog: only when a provider is mounted AND the target is
//     a dialog slug. Production does not mount a provider — this repo
//     has no M3 confirm dialog for a nudge — so Review stays disabled.
//     A mounted provider must open the agent-chat confirmation path
//     (server-issued tool call, operator confirm, then
//     confirmations posted to /api/agent/chat). It must not POST itself.
//   • navigate: only to an allowlisted in-app path. The destination
//     owns whatever confirm it already has. Disallowed targets no-op.
//
// NEVER a direct POST/PUT/DELETE. Pinned by AT-S7.
//
// Wrapped in an error boundary (AT-S14) so a thrown error inside the
// card body (e.g. bad prefill, broken emoji in title) cannot blank
// the surface. The boundary lives in this file to keep AT-S14's
// per-nudge isolation local to the component the test can wrap.

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Button, CloseButton, HStack, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import type { Nudge } from './types';
import { AGENT_SHELL_VISUAL_STATUS } from './types';
import { allowlistedDialogKey, allowlistedNavigateTarget } from './actionAllowlist';
import { useAgentShellDialog } from './AgentShellDialogContext';

type Props = {
  nudge:      Nudge;
  // Called after the operator clicks the close ×. Doesn't mutate
  // anything server-side — the parent chooses what "dismissed" means
  // (filter the nudge out of the next render; stash the id in local
  // state; emit an audit event). D-S14 impressions/dismissals go
  // through the agent-audit writer in M7.4, not here.
  onDismiss?: () => void;
};

export function AgentInlineNudge({ nudge, onDismiss }: Props) {
  return (
    <AgentInlineNudgeErrorBoundary nudge={nudge}>
      <AgentInlineNudgeBody nudge={nudge} onDismiss={onDismiss} />
    </AgentInlineNudgeErrorBoundary>
  );
}

function AgentInlineNudgeBody({ nudge, onDismiss }: Props) {
  const navigate = useNavigate();
  const dialog   = useAgentShellDialog();
  const flow     = nudge.confirmFlow;
  const dialogKey = flow?.kind === 'open-dialog' ? allowlistedDialogKey(flow.target) : null;
  const navigateTarget = flow?.kind === 'navigate' ? allowlistedNavigateTarget(flow.target) : null;
  // open-dialog is disabled until a real provider is mounted. The
  // production tree does not mount one, so Review cannot fire.
  const canReview = (flow?.kind === 'open-dialog' && dialog != null && dialogKey != null)
    || (flow?.kind === 'navigate' && navigateTarget != null);

  const handleCta = () => {
    if (!flow || !canReview) return;

    if (flow.kind === 'open-dialog') {
      if (!dialog || !dialogKey) return;
      dialog.open(dialogKey, flow.prefill);
      return;
    }
    if (flow.kind === 'navigate') {
      if (!navigateTarget) return;
      navigate(navigateTarget, { state: flow.prefill });
    }
  };

  return (
    <Box
      role="region"
      aria-label={`Agent nudge: ${nudge.title}`}
      borderWidth="1px"
      borderColor="rsViolet.200"
      bg="rsViolet.50"
      borderLeftWidth="4px"
      borderLeftColor="rsViolet.400"
      borderRadius="md"
      px={4}
      py={3}
      data-testid={`agent-inline-nudge-${nudge.id}`}
      data-nudge-kind={nudge.kind}
      data-design-status={AGENT_SHELL_VISUAL_STATUS}
    >
      <HStack align="flex-start" spacing={3}>
        <VStack align="stretch" spacing={1} flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="700" color="rsViolet.700">
            {nudge.title}
          </Text>
          <Text fontSize="xs" color="brand.ink" whiteSpace="pre-wrap">
            {nudge.body}
          </Text>
          <HStack spacing={2} pt={2}>
            <Button
              size="xs"
              colorScheme="purple"
              onClick={handleCta}
              isDisabled={!canReview}
              data-testid={`agent-inline-nudge-cta-${nudge.id}`}
            >
              Review
            </Button>
          </HStack>
        </VStack>
        {onDismiss && (
          <CloseButton
            size="sm"
            onClick={onDismiss}
            aria-label="Dismiss nudge"
            data-testid={`agent-inline-nudge-dismiss-${nudge.id}`}
          />
        )}
      </HStack>
    </Box>
  );
}

// Per-nudge error boundary (AT-S14). A render-time throw inside one
// nudge card produces a muted fallback and does NOT bubble into the
// surface. The fallback is intentionally terse — nudges are a lens,
// not a surface, so a loud error state would overstate the failure.
type BoundaryProps = {
  nudge:    Nudge;
  children: ReactNode;
};

type BoundaryState = { error: Error | null };

class AgentInlineNudgeErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('AgentInlineNudge render error:', this.props.nudge.id, error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          borderWidth="1px"
          borderColor="brand.border"
          bg="brand.surface"
          borderRadius="md"
          px={3}
          py={2}
          data-testid={`agent-inline-nudge-error-${this.props.nudge.id}`}
        >
          <Text fontSize="xs" color="brand.muted">
            Agent suggestion unavailable.
          </Text>
        </Box>
      );
    }
    return this.props.children;
  }
}
